import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import {
    Grid,
    Button,
    CircularProgress,
    IconButton,
} from '@material-ui/core';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CustomTable from 'components/CustomTable'
import SearchBar from 'components/CustomTable/searchbar'
import Filters from 'components/CustomTable/filters'
import { prepareTableDisplay, tableOptions } from 'pages/common';
import useStyles from 'pages/styles';

const DataTable = ({ intl: { formatMessage },
    openFilter,
    setOpenFilter,
    setFilters,
    keyword,
    setKeyword,
    loadMore,
    allUsersLoaded,
    dataLoading,
    tableColumns,
    datasource,
    data,
    counts,
    rightComponent,
    customTableOptions,
    noMargin,
    hasFilter,
    showKeywordSearch = true,
    pagination, // optional: { page, perPage, total, onChange }
    activeFilters,
}) => {
    const classes = useStyles();
    const { data: tableData, options: tableOpts } = prepareTableDisplay({
        formatMessage,
        dataLoading,
        datasource,
        options: customTableOptions || tableOptions(formatMessage),
    });
    return (
        <Grid container >
            <Grid item sm={openFilter ? 9 : 12} style={{ padding: '0px !important' }}>
                {
                    setOpenFilter &&
                    <SearchBar
                        openFilter={openFilter}
                        setOpenFilter={setOpenFilter}
                        keyword={keyword}
                        setKeyword={setKeyword}
                        counts={counts}
                        rightComponent={rightComponent}
                        hasFilter={hasFilter}
                        showKeywordSearch={showKeywordSearch}
                        activeFilters={activeFilters}
                    />
                }
                <div className={!noMargin && classes.table}>
                    <CustomTable
                        columns={tableColumns}
                        data={tableData}
                        options={tableOpts}
                    />
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20, gap: 10 }}>
                        {pagination ? (
                            (() => {
                                const total = pagination.total || 0;
                                const perPage = pagination.perPage || 30;
                                const pageNum = pagination.page || 1;
                                const totalPages = Math.max(1, Math.ceil(total / perPage));
                                const start = total === 0 ? 0 : (pageNum - 1) * perPage + 1;
                                const end = Math.min(total, pageNum * perPage);
                                const maxButtons = 5;
                                let startPage = Math.max(1, pageNum - Math.floor(maxButtons / 2));
                                let endPage = startPage + maxButtons - 1;
                                if (endPage > totalPages) {
                                    endPage = totalPages;
                                    startPage = Math.max(1, endPage - maxButtons + 1);
                                }
                                const pages = [];
                                for (let p = startPage; p <= endPage; p += 1) pages.push(p);

                                return (
                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', gap: 12 }}>
                                        <div style={{ marginRight: 'auto', paddingLeft: 12, color: '#666' }}>{`${start} - ${end} of ${total}`}</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <IconButton aria-label="first" onClick={() => pagination.onChange?.(1)} disabled={pageNum <= 1}>
                                                <FirstPageIcon />
                                            </IconButton>
                                            <IconButton aria-label="prev" onClick={() => pagination.onChange?.(Math.max(1, pageNum - 1))} disabled={pageNum <= 1}>
                                                <ChevronLeftIcon />
                                            </IconButton>
                                            {startPage > 1 && (
                                                <Button size="small" onClick={() => pagination.onChange?.(1)}>1</Button>
                                            )}
                                            {startPage > 2 && (
                                                <span style={{ padding: '0 6px' }}>…</span>
                                            )}
                                            {pages.map(p => (
                                                <Button key={p} variant={p === pageNum ? 'contained' : 'text'} color={p === pageNum ? 'primary' : 'default'} size="small" onClick={() => pagination.onChange?.(p)}>                                                    {p}
                                                </Button>
                                            ))}
                                            {endPage < totalPages - 1 && (
                                                <span style={{ padding: '0 6px' }}>…</span>
                                            )}
                                            {endPage < totalPages && (
                                                <Button size="small" onClick={() => pagination.onChange?.(totalPages)}>{totalPages}</Button>
                                            )}
                                            <IconButton aria-label="next" onClick={() => pagination.onChange?.(Math.min(totalPages, pageNum + 1))} disabled={pageNum >= totalPages}>                                                
                                                <ChevronRightIcon />
                                            </IconButton>
                                            <IconButton aria-label="last" onClick={() => pagination.onChange?.(totalPages)} disabled={pageNum >= totalPages}>
                                                <LastPageIcon />
                                            </IconButton>
                                        </div>
                                    </div>
                                );
                            })()
                        ) : (
                            loadMore && (
                                dataLoading && data && data.length > 0 ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <CircularProgress size={24} />
                                        {formatMessage({ id: 'muitable.placeholder.loading' })}
                                    </div>
                                ) : (
                                    !allUsersLoaded && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={loadMore}
                                            disabled={!(data && data.length > 0)}
                                        >
                                            {formatMessage({ id: 'home.users.LoadMore', defaultMessage: 'Load More' })}
                                        </Button>
                                    )
                                )
                            )
                        )}
                    </div>
                </div>
            </Grid>
            {
                openFilter &&
                <Filters
                    setOpenFilter={setOpenFilter}
                    setOnFilter={setFilters}
                    columns={tableColumns}
                    data={data}
                />
            }
        </Grid>
    );
};

DataTable.propTypes = {
    intl: PropTypes.shape({
        formatMessage: PropTypes.func.isRequired,
    }),
    openFilter: PropTypes.bool,
    setOpenFilter: PropTypes.func,
    setFilters: PropTypes.func,
    keyword: PropTypes.string,
    setKeyword: PropTypes.func,
    loadMore: PropTypes.func,
    allUsersLoaded: PropTypes.bool,
    dataLoading: PropTypes.bool,
    tableColumns: PropTypes.any,
    datasource: PropTypes.any,
    data: PropTypes.any,
    counts: PropTypes.any,
    rightComponent: PropTypes.any,
    customTableOptions: PropTypes.any,
    noMargin: PropTypes.bool,
    hasFilter: PropTypes.bool,
    showKeywordSearch: PropTypes.bool,
    pagination: PropTypes.any,
    activeFilters: PropTypes.array,
};

export default injectIntl(DataTable);
